/**
 * Design tokens — MUI-style palette architecture.
 *
 * Each semantic group owns its own state tokens (hover, disabled, active)
 * pre-computed via MUI utils. No magic numbers per-component, no opacity bleed-through.
 *
 * Usage: theme.colors.primary.main → theme.colors.primary.hover → theme.colors.primary.disabled
 *
 * This is the foundation for SCSS decoupling. Emotion-based components import from here.
 * When a ThemeProvider is added later, the augmented @emotion/react Theme type ensures
 * ${({ theme }) => theme.colors.x.y} is fully typed with no further changes.
 */

import { alpha, lighten } from '@mui/material/styles'

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

export const colors = {
  // Primary action — dark charcoal buttons and primary interactive elements.
  // hover/disabled pre-computed; no state tokens needed in components.
  primary: {
    main: '#141414',
    hover: lighten('#141414', 0.1),
    disabled: alpha('#141414', 0.12),
  },

  // Danger — destructive actions (delete buttons, error states)
  danger: {
    main: '#d01145',
    hover: lighten('#d01145', 0.1),
    disabled: alpha('#d01145', 0.12),
    subtle: '#fde7ed',
  },

  // Status — informational only (alerts, badges — not used for interactive elements)
  warning: { main: '#b28600', subtle: '#fff6e6' },
  info: { main: '#0d5173', subtle: '#d8f0fd' },
  success: { main: '#257f3c', subtle: '#e2f8ef' },

  // Brand — logos and brand-specific badges. Not used for actions.
  brand: { main: '#2b8c42' },

  // Text
  text: {
    primary: '#141414',
    secondary: '#676767',
    disabled: alpha('#141414', 0.38),
  },

  // Backgrounds
  background: {
    default: '#fff',
    subtle: '#f1f1f1',
    hover: alpha('#000000', 0.04),
    active: '#e0e0e0',
    disabled: alpha('#000000', 0.12),
  },

  // Borders — dividers, input strokes, focus rings
  border: {
    line: 'rgba(0, 0, 0, 0.12)',
    strong: 'rgba(0, 0, 0, 0.19)',
    stroke: '#d8d8df',
    active: '#141414',
    disabled: alpha('#000000', 0.12),
  },

  // Links
  link: {
    default: '#141414',
    hover: lighten('#141414', 0.1),
    disabled: alpha('#141414', 0.38),
    subtle: '#3a3a3a',
  },

  // Misc structural
  shadow: 'rgba(17, 17, 17, 0.13)',
} as const

// ---------------------------------------------------------------------------
// Space — pure spacing scale
// ---------------------------------------------------------------------------

export const space = {
  '2xs': '2px',
  xs: '4px',
  s: '8px',
  m: '12px',
  l: '16px',
  xl: '24px',
  '2xl': '32px',
} as const

// ---------------------------------------------------------------------------
// Radii — separated from spacing
// ---------------------------------------------------------------------------

export const radii = {
  sm: '6px',
  md: '8px',
  lg: '14px',
  xl: '24px',
} as const

// ---------------------------------------------------------------------------
// Supporting tokens
// ---------------------------------------------------------------------------

export const borders = {
  width: '1px',
} as const

export const breakpoints = {
  md: '768px',
} as const

export const fontFamilies = {
  heading: "'Inter', sans-serif",
  body: "'Inter', sans-serif",
} as const

// ---------------------------------------------------------------------------
// Component tokens — values reused across multiple components
// ---------------------------------------------------------------------------

export const components = {
  badge: {
    green: '#c7ecdc',
    orange: '#f4f2c0',
    red: '#f2ced4',
    tag: '#dfdfe3',
  },
  icon: {
    default: '#676767',
    strong: '#3a3a3a',
    info: '#0d5173',
    danger: '#d01145',
    warning: '#9a6700',
    success: '#175e41',
  },
} as const

// ---------------------------------------------------------------------------
// Composite export
// ---------------------------------------------------------------------------

export const theme = {
  colors,
  space,
  radii,
  borders,
  breakpoints,
  fontFamilies,
  components,
} as const

// ---------------------------------------------------------------------------
// ThemeProvider preparation
//
// Augmenting @emotion/react's Theme interface means that when a ThemeProvider
// is added to the app, all styled components using
//   ${({ theme }) => theme.colors.primary.main}
// will have full type inference with no further changes.
// ---------------------------------------------------------------------------

export type AppTheme = typeof theme

declare module '@emotion/react' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface Theme extends AppTheme {}
}
